import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { graphql, useStaticQuery } from 'gatsby';
import styled from '@emotion/styled';
import { useWindowScroll, useMedia, usePrevious } from 'react-use';
import { useTheme } from '@emotion/react';

import useUserContext from '../../hooks/use-user-context';
import { createOptionMap } from '../../utilities/misc';
import { rem } from '../../utilities/style';
import { colors } from '../../theme/colors';
import { breakpoints, media } from '../../theme/media'; // breakpoints,
import stacking from '../../theme/stacking';
import { fontWeights } from '../../theme/type';
import { containerStyles } from '../../styles/layout';

import Button from '../buttons/Button';
import { ReactComponent as IconPerson } from '../../images/icons/person.svg';
import { ReactComponent as IconPhone } from '../../images/icons/phone.svg';
import Link from '../Link';
import logoImage from '../../images/logo.svg';
import logoImageSSW from '../../images/ssw-logo.jpg';
import logoImageFP from '../../images/fp-logo.png';
import logoDarkModeImage from '../../images/logo-dark-mode.svg';
import NavbarLanguageSwitcher from './NavbarLanguageSwitcher';
import NavbarMainMenu from './NavbarMainMenu';
import NavbarMainUtilityMenu from './NavbarMainUtilityMenu';
import NavbarSlideOutMenu from './NavbarSlideOutMenu';
import BackToHome from '../BackToHome';
import TextRenderer from '../TextRenderer';
import VariableText from '../VariableText';

const NavbarSpacer = styled.div`
  padding-top: ${rem(56)};

  ${media('>small')} {
    padding-top: ${rem(70)};
  }

  ${media('>nav')} {
    padding-top: ${rem(90)};
  }
`;

const Container = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: ${stacking.navbar};

  ${media('>nav')} {
    &::before {
      content: '';
      position: fixed;
      inset: 0;
      background: ${colors.blue['800']};
      z-index: -1;
      pointer-events: none;
      opacity: 0;
      transition: opacity 200ms ease;

      ${({ dropdownOpen }) => dropdownOpen && `
        opacity: 0.8;
      `}
    }
  }
`;

const NavbarContainer = styled.div`
  height: ${rem(56)};
  box-shadow: none;
  transition: all .2s ease;
  height: ${rem(56)};
  background: ${colors.white};

  ${media('>small')} {
    height: ${rem(70)};
  }

  ${media('>nav')} {
    height: ${rem(90)};
  }

  ${({ scrolled, theme }) => scrolled && `
    box-shadow: 0 1px 0 rgba(0, 0, 0, .10);

    ${theme.pageOverrides?.includes('Dark Mode') && `
      box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.16), 0px 2px 8px 0px rgba(0, 0, 0, 0.16);
      background-color: ${colors.blue['800']};
    `}
  `}
`;

const ContentWrapper = styled.div`
  ${containerStyles.regular}
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 100%;
`;

const LogoLink = styled(Link)`
  max-width: ${rem(158)};

  ${media('>nav')} {
    transform: translateY(${rem(-2)});
  }

  ${media('>medium')} {
    max-width: none;
  }
`;

const LogoLinkSSW = styled(Link)`
  // When the header is 56px tall
  max-width: ${rem(180)};

  // When the header is 70px tall
  ${media('>small')} {
    max-width: ${rem(215)};
  }

  // When the header is 90px tall
  ${media('>nav')} {
    max-width: ${rem(245)};
    transform: translateY(${rem(-2)});
  }

  img {
    height: auto;
  }
`;

const LogoLinkFP = styled(Link)`
  // When the header is 56px tall
  max-width: ${rem(180)};

  // When the header is 70px tall
  ${media('>small')} {
    max-width: ${rem(215)};
  }

  // When the header is 90px tall
  ${media('>nav')} {
    max-width: ${rem(245)};
    transform: translateY(${rem(-2)});
  }

  img {
    height: auto;
  }
`;

const Logo = styled.img``;

const MobileMenu = styled.div`

  ${media('>=medium')} {
    display: none;

    @media (hover: none) {
      display: block;
    }
  }
`;

const DesktopMenu = styled.div`
  display: none;
  position: relative;
  z-index: ${stacking.navbar + 1};

  ${media('>=medium')} {
    display: flex;
    align-items: center;
    padding-left: ${rem(42)};
    ${({ fillWidth }) => fillWidth && 'flex: 1;'}

    @media (hover: none) {
      display: none;
    }
  }

  ${({ scrolled }) => scrolled && `
    z-index: inherit;
  `}
`;

const ScrollMenuWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  ${({ visible }) => !visible && `
    display: none;
  `}
`;

const ScrollCtaWrapper = styled.div`
  display: flex;
  align-items: center;

  ${({ visible }) => !visible && `
    display: none;
  `}
`;

const PhoneNumber = styled.a`
  color: ${colors.blue['800']};
  font-weight: ${fontWeights.semiBold};
  margin-right: ${rem(24)};
  text-decoration: underline;
  display: inline-flex;
  align-items: center;

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.blue['100']};
  `}

  &:hover {
    text-decoration: none;
  }
`;

const IconLinks = styled.div`
  display: flex;
  // margin-left: ${rem(10)};
  // margin-right: ${rem(-10)};
  margin: 0 ${rem(12.5)};
  ${media('>nav')} and ${media('<medium')}  {
  // margin-left: ${rem(20)};
    margin: 0 ${rem(5)};
  }

  // ${media('>large')} {
  //   margin-left: ${rem(30)};
  //   margin: 0 ${rem(-10)};
  // }
`;

const IconLink = styled(Link)`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  height: ${rem(90)};
  padding: 0 ${rem(10)};
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const IconPersonStyled = styled(IconPerson)`
  color: ${colors.blue['800']};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.blue['100']};
  `}
`;

const IconPhoneStyled = styled(IconPhone)`
  color: ${colors.black};
  margin-right: ${rem(14)};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.blue['100']};
  `}
`;

const Navbar = ({ type = 'Normal' }) => {
  const data = useStaticQuery(graphql`
    query Navbar {
      allContentfulOptionSet(filter: {name: {regex: "/^(globals|navbar)$/"}}) {
        nodes {
          ...OptionSetFragment
        }
      }
      allAccountNavigation: allContentfulNavigation(
        filter: {location: {eq: "account"}}
      ) {
        nodes {
          ...NavigationFragment
        }
      }
    }
  `);
  const { language } = useUserContext();
  const options = createOptionMap(data.allContentfulOptionSet.nodes, language);
  const { y: windowY } = useWindowScroll();
  const isTouch = useMedia('(hover: none)');
  const isNavBreakpoint = useMedia(`(min-width: ${rem(breakpoints.medium)})`, true);
  const isNotSimpleType = type !== 'Simple';
  const [isScrolled, setIsScrolled] = useState(false);
  const [isScrollingDown, setIsScrollingDown] = useState(false);
  const lastWindowY = usePrevious(windowY);
  const [scrollDistance, setScrollDistance] = useState(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Default menu options
  let hasPhoneNumber = true;
  let accountIconLink = 'https://redirect.splashtop.com/my/login';
  let ctaButtonText = options['navbar.scroll_cta_button_text'];
  let ctaButtonLink = options['navbar.scroll_cta_button_url'];
  let hasCtaButton = ctaButtonText && ctaButtonLink;

  // Override menu options for Simple menu
  if (type === 'Simple') {
    hasPhoneNumber = false;
    accountIconLink = '';
    hasCtaButton = false;
  }

  // Override menu options for Secure Workspace
  if (type === 'Secure Workspace') {
    hasPhoneNumber = false;
    accountIconLink = 'https://us.ssw.splashtop.com/#/downloads';
    ctaButtonText = options['globals.text_get_started'];
    ctaButtonLink = (process.env.CONTENTFUL_ENVIRONMENT === 'web-dev' ? 'https://register.stage.ztw.splashtop.com' : 'https://reg.ssw.splashtop.com');
  }

  useEffect(() => {
    let mounted = true;
    let timeout = null;

    const reset = () => {
      if (timeout !== null) {
        clearTimeout(timeout);
        timeout = null;
      }
    };

    if (windowY > 100 && lastWindowY && windowY >= lastWindowY) {
      setScrollDistance((prevDistance) => prevDistance + windowY - lastWindowY);
      timeout = setTimeout(() => {
        if (mounted) {
          setScrollDistance(0);
        }
      }, 75);
    } else {
      setScrollDistance(0);
      reset();
    }

    if (scrollDistance > 80) {
      setIsScrollingDown(true);
      reset();
    } else {
      setIsScrollingDown(false);
    }

    return () => {
      mounted = false;
      reset();
    };
  }, [windowY, lastWindowY, scrollDistance]);

  useEffect(() => {
    setIsScrolled(windowY > 20);
  }, [windowY]);

  const darkMode = useTheme().pageOverrides?.includes('Dark Mode');
  const isMicrosite = ['Secure Workspace', 'Foxpass'].includes(type);
  return (
    <>
      <NavbarSpacer />
      <Container dropdownOpen={isDropdownOpen}>
        {isMicrosite && <BackToHome scrolled={isScrolled} />}
        <NavbarContainer scrolled={isScrolled}>
          <ContentWrapper>
            {type !== 'Secure Workspace' && type !== 'Foxpass' && (
              <LogoLink to="/" aria-label={options['globals.go_to_home']}>
                <Logo
                  src={darkMode ? logoDarkModeImage : logoImage}
                  width="191"
                  height="38"
                  alt="Splashtop"
                />
              </LogoLink>
            )}
            {type === 'Secure Workspace' && (
              <LogoLinkSSW to="/secure" aria-label={options['globals.go_to_home']}>
                <Logo
                  src={logoImageSSW}
                  width="245"
                  height="53"
                  alt="Splashtop Secure Workspace"
                />
              </LogoLinkSSW>
            )}
            {type === 'Foxpass' && (
              <LogoLinkFP to="/foxpass" aria-label={options['globals.go_to_home']}>
                <Logo
                  src={logoImageFP}
                  width="191"
                  height="38"
                  alt="Foxpass"
                />
              </LogoLinkFP>
            )}
            <MobileMenu aria-hidden={isNavBreakpoint && !isTouch}>
              <NavbarSlideOutMenu
                type={type}
                hasPhoneNumber={hasPhoneNumber}
                accountIconLink={accountIconLink}
                ctaButtonText={ctaButtonText}
                ctaButtonLink={ctaButtonLink}
                hasCtaButton={hasCtaButton}
                isMicrosite={isMicrosite}
                scrolled={isScrolled}
              />
            </MobileMenu>
            <DesktopMenu
              fillWidth={isNotSimpleType}
              scrolled={isScrolled}
              aria-hidden={!isNavBreakpoint && isTouch}
            >
              {isNotSimpleType && (
                <NavbarMainMenu
                  type={type}
                  setIsDropdownOpen={setIsDropdownOpen}
                />
              )}
              <ScrollMenuWrapper visible={!isScrollingDown || !hasCtaButton}>
                {isNotSimpleType && (
                  <NavbarMainUtilityMenu
                    type={type}
                    setIsDropdownOpen={setIsDropdownOpen}
                  />
                )}
                <NavbarLanguageSwitcher
                  setIsDropdownOpen={setIsDropdownOpen}
                />
                {accountIconLink && (
                  <IconLinks>
                    <IconLink
                      to={accountIconLink}
                      aria-label={options['navbar.account_button_label']}
                    >
                      <IconPersonStyled />
                    </IconLink>
                  </IconLinks>
                )}
                {hasCtaButton && (
                  <Button to={ctaButtonLink} size="small">
                    <TextRenderer text={ctaButtonText} />
                  </Button>
                )}
              </ScrollMenuWrapper>
              <ScrollCtaWrapper visible={isScrollingDown}>
                {hasPhoneNumber && (
                  <VariableText
                    type="phone-number"
                    name="sales-support"
                    render={(phoneNumbers) => (
                      <PhoneNumber href={`tel:${phoneNumbers.salesPhone}`}>
                        <IconPhoneStyled />
                        {phoneNumbers.salesPhone}
                      </PhoneNumber>
                    )}
                  />
                )}
                {hasCtaButton && (
                  <Button to={ctaButtonLink} size="small">
                    <TextRenderer text={ctaButtonText} />
                  </Button>
                )}
              </ScrollCtaWrapper>
            </DesktopMenu>
          </ContentWrapper>
        </NavbarContainer>
      </Container>
    </>
  );
};

Navbar.propTypes = {
  type: PropTypes.oneOf(['Normal', 'Simple', 'Secure Workspace'])
};

export default Navbar;
